import React, { useEffect, useState, useMemo, useCallback } from "react";
import HttpService from "../../services/httpService";
import { setSnackInfo } from "../../redux/actions/appAction";
import { connect } from "react-redux";
import {
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TableSortLabel,
    Paper,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
    LinearProgress,
    Tooltip as MuiTooltip,
    withStyles,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { CSVLink } from "react-csv";
import moment from "moment";
import AccountBalanceWalletOutlinedIcon from "@material-ui/icons/AccountBalanceWalletOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import LocationCityOutlinedIcon from "@material-ui/icons/LocationCityOutlined";
import DateRangeOutlinedIcon from "@material-ui/icons/DateRangeOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import GetAppIcon from "@material-ui/icons/GetApp";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import TuneIcon from "@material-ui/icons/Tune";

const ORANGE = "#FB8C00";
const ORANGE_DARK = "#d97400";
const BLUE = "#2a78d6";
const TEAL = "#0d9488";
const PURPLE = "#7c5cd6";
const GOOD = "#0f9d58";
const BAD = "#d93b3b";
const NEUTRAL = "#8b93a6";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 58%, #ffab40 100%)`;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const GRAND_TOTAL = "grand total";
const isGrandTotalRow = (row) => String(row?.cityName || "").trim().toLowerCase() === GRAND_TOTAL;

const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const formatMoney = (value, decimals = 0) =>
    `₹${toNumber(value).toLocaleString("en-IN", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })}`;

const formatCount = (value) => toNumber(value).toLocaleString("en-IN");

const cplOf = (row) => {
    const leads = toNumber(row?.totalLeads);
    return leads > 0 ? toNumber(row?.spend) / leads : null;
};

const useStyles = makeStyles(() => ({
    pageRoot: {
        background: "linear-gradient(180deg, #F7F9FC 0%, #EFF3F9 100%)",
        minHeight: "100vh",
        padding: "14px 18px 32px",
    },
    banner: {
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
        padding: "20px 26px",
        borderRadius: 18,
        marginBottom: 16,
        background: HEADER_GRADIENT,
        boxShadow: "0 12px 30px rgba(217,116,0,0.26)",
        "&::after": {
            content: '""',
            position: "absolute",
            top: -80,
            right: -30,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0) 70%)",
            pointerEvents: "none",
        },
        "& > *": { position: "relative", zIndex: 1 },
    },
    bannerIcon: {
        width: 50,
        height: 50,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        background: "rgba(255,255,255,0.18)",
        border: "2px solid rgba(255,255,255,0.32)",
        color: "#fff",
    },
    bannerTitle: { fontSize: 20, fontWeight: 700, color: "#fff", letterSpacing: 0.2 },
    bannerSubtitle: { fontSize: 13, color: "rgba(255,255,255,0.8)", marginTop: 4 },
    bannerChip: {
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "8px 16px",
        borderRadius: 20,
        background: "rgba(255,255,255,0.18)",
        color: "#fff",
        fontWeight: 700,
        fontSize: 13,
        whiteSpace: "nowrap",
    },
    bannerRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 },
    refreshButton: {
        color: "#fff",
        backgroundColor: "rgba(255,255,255,0.18)",
        border: "1px solid rgba(255,255,255,0.32)",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
    },
    "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    spinning: { animation: "$spin 0.9s linear infinite" },
    lastUpdated: { fontSize: 11.5, color: "rgba(255,255,255,0.78)", whiteSpace: "nowrap" },

    filterCard: {
        padding: "16px 20px",
        marginBottom: 16,
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
    },
    filterTitle: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: ORANGE_DARK,
        fontWeight: 700,
        fontSize: 13,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: 14,
    },
    filterRow: { display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" },
    field: {
        "& .MuiOutlinedInput-root": { borderRadius: 10 },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ORANGE },
        "& .MuiInputLabel-root.Mui-focused": { color: ORANGE_DARK },
    },
    primaryBtn: {
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE})`,
        color: "#fff",
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 10,
        padding: "8px 22px",
        boxShadow: "0 4px 12px rgba(217,116,0,0.3)",
        "&:hover": { background: `linear-gradient(135deg, #c96b00, ${ORANGE_DARK})` },
        "&.Mui-disabled": { background: "#e3c8a8", color: "#fff" },
    },
    csvBtn: {
        background: "linear-gradient(135deg, #146c43, #1e8449)",
        color: "#fff",
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 10,
        padding: "8px 20px",
        textDecoration: "none",
        boxShadow: "0 4px 12px rgba(20,108,67,0.28)",
        "&:hover": { background: "linear-gradient(135deg, #0f5433, #166b3a)" },
        "&.Mui-disabled": { background: "#c3d4c9", color: "#fff", boxShadow: "none" },
    },

    statCard: {
        height: "100%",
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 10px 26px rgba(28,55,93,0.12)" },
    },
    statInner: { display: "flex", alignItems: "center", gap: 14, padding: "16px 18px !important" },
    statIcon: {
        width: 46,
        height: 46,
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    statLabel: {
        fontSize: 11.5,
        fontWeight: 700,
        color: "#8b93a6",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    statValue: { fontSize: 23, fontWeight: 700, color: "#1f2d3d", marginTop: 3, lineHeight: 1.15 },
    statSub: { fontSize: 11.5, color: "#9aa4b4", marginTop: 3 },

    tableCard: {
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
        overflow: "hidden",
    },
    tableToolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
        padding: "16px 18px 14px",
    },
    tableTitle: {
        display: "flex",
        alignItems: "center",
        gap: 9,
        fontSize: 15,
        fontWeight: 700,
        color: "#1f2d3d",
        "&::before": {
            content: '""',
            width: 4,
            height: 16,
            borderRadius: 3,
            background: `linear-gradient(180deg, ${ORANGE}, ${ORANGE_DARK})`,
        },
    },
    tableSubtitle: { fontSize: 12, color: "#8b93a6", marginTop: 3, paddingLeft: 13 },
    searchField: {
        "& .MuiOutlinedInput-root": { borderRadius: 999, backgroundColor: "#fff" },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ORANGE },
    },
    tableWrap: { maxHeight: 560, overflow: "auto" },
    headCell: {
        background: `linear-gradient(135deg, ${ORANGE_DARK} 0%, #c26a00 100%)`,
        color: "#FFF2EB",
        fontWeight: 600,
        fontSize: 11.5,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        border: "none",
        padding: "13px 16px",
    },
    bodyCell: {
        borderBottom: "1px solid #edf0f5",
        padding: "12px 16px",
        fontSize: 13.5,
        color: "#41506b",
    },
    bodyRow: {
        transition: "background-color 0.15s ease",
        "&:nth-of-type(even)": { backgroundColor: "#FBFCFE" },
        "&:hover": { backgroundColor: "#FFF6EC" },
    },
    totalRow: {
        background: "#FFF3E3 !important",
        "& td": {
            fontWeight: 700,
            color: "#8a4b00",
            borderTop: `2px solid ${ORANGE}`,
            fontSize: 14,
        },
    },
    cityName: { fontWeight: 600, color: "#2b3648" },
    shareBarTrack: {
        height: 4,
        borderRadius: 3,
        background: "#edf0f5",
        marginTop: 6,
        overflow: "hidden",
    },
    shareBarFill: { height: "100%", borderRadius: 3, background: ORANGE },
    cplChip: {
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 7,
        fontWeight: 700,
        fontSize: 12.5,
    },
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "44px 20px",
        color: "#9aa4b4",
        textAlign: "center",
    },
    emptyIcon: { fontSize: 36, color: "#c7cedb", marginBottom: 8 },
    legend: {
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
        padding: "10px 18px 14px",
        fontSize: 11.5,
        color: "#8b93a6",
    },
    legendItem: { display: "flex", alignItems: "center", gap: 5 },
    legendDot: { width: 9, height: 9, borderRadius: "50%" },
}));

const SortLabel = withStyles({
    root: {
        color: "#FFF2EB",
        "&:hover": { color: "#fff" },
        "&$active": { color: "#fff" },
    },
    active: {},
    icon: { color: "inherit !important" },
})(TableSortLabel);

const COLUMNS = [
    { id: "cityName", label: "City Name", align: "left", numeric: false },
    { id: "spend", label: "Spend (₹)", align: "right", numeric: true },
    { id: "totalLeads", label: "Leads", align: "right", numeric: true },
    { id: "cpl", label: "CPL (₹)", align: "right", numeric: true },
];

const StatCard = ({ label, value, sub, accent, icon, loading, classes }) => (
    <Card className={classes.statCard}>
        <CardContent className={classes.statInner}>
            <div className={classes.statIcon} style={{ backgroundColor: `${accent}1a`, color: accent }}>
                {icon}
            </div>
            <div style={{ minWidth: 0 }}>
                <div className={classes.statLabel}>{label}</div>
                {loading ? (
                    <Skeleton variant="text" width={110} height={30} />
                ) : (
                    <>
                        <div className={classes.statValue}>{value}</div>
                        {sub ? <div className={classes.statSub}>{sub}</div> : null}
                    </>
                )}
            </div>
        </CardContent>
    </Card>
);

const SpendReport = (props) => {
    const classes = useStyles();

    const currentYear = new Date().getFullYear();
    const yearList = useMemo(() => Array.from({ length: 4 }, (_, i) => currentYear - i), [currentYear]);

    const [store, setStore] = useState([]);
    const [month, setMonth] = useState(MONTHS[new Date().getMonth()]);
    const [year, setYear] = useState(currentYear);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("spend");
    const [sortDir, setSortDir] = useState("desc");
    // The period actually reflected by the rows on screen, so the heading never
    // claims a month the user has only selected but not yet fetched.
    const [loadedPeriod, setLoadedPeriod] = useState({ month: MONTHS[new Date().getMonth()], year: currentYear });

    const { setSnackInfo } = props;

    const spendReport = useCallback(async () => {
        setLoading(true);
        try {
            const response = await HttpService.getSpendReport({
                startDate: "",
                endDate: "",
                month,
                year,
            });
            setStore(Array.isArray(response?.data?.data) ? response.data.data : []);
            setLoadedPeriod({ month, year });
            setLastUpdated(new Date());
        } catch (error) {
            setStore([]);
            setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error");
        }
        setLoading(false);
    }, [month, year, setSnackInfo]);

    useEffect(() => {
        spendReport();
        // Intentionally on mount only - afterwards the user drives it with Get Report.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const grandTotalRow = useMemo(() => store.find(isGrandTotalRow), [store]);
    const cityRows = useMemo(() => store.filter((row) => !isGrandTotalRow(row)), [store]);

    const totals = useMemo(() => {
        const spend = grandTotalRow
            ? toNumber(grandTotalRow.spend)
            : cityRows.reduce((sum, row) => sum + toNumber(row.spend), 0);
        const leads = grandTotalRow
            ? toNumber(grandTotalRow.totalLeads)
            : cityRows.reduce((sum, row) => sum + toNumber(row.totalLeads), 0);
        return { spend, leads, cpl: leads > 0 ? spend / leads : 0, cities: cityRows.length };
    }, [grandTotalRow, cityRows]);

    const visibleRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        const filtered = term
            ? cityRows.filter((row) => String(row.cityName || "").toLowerCase().includes(term))
            : cityRows;

        const direction = sortDir === "asc" ? 1 : -1;
        return [...filtered].sort((a, b) => {
            if (sortBy === "cityName") {
                return String(a.cityName || "").localeCompare(String(b.cityName || "")) * direction;
            }
            // Cities with no leads have no meaningful CPL - keep them last either way.
            const aVal = sortBy === "cpl" ? cplOf(a) : toNumber(a[sortBy]);
            const bVal = sortBy === "cpl" ? cplOf(b) : toNumber(b[sortBy]);
            if (aVal === null) return 1;
            if (bVal === null) return -1;
            return (aVal - bVal) * direction;
        });
    }, [cityRows, search, sortBy, sortDir]);

    const handleSort = (columnId) => {
        if (sortBy === columnId) {
            setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
            return;
        }
        setSortBy(columnId);
        setSortDir(columnId === "cityName" ? "asc" : "desc");
    };

    // Mirrors exactly what is on screen (current search + sort), with the
    // Grand Total appended so the export still foots.
    const csvData = useMemo(() => {
        const rows = visibleRows.map((row, index) => {
            const cpl = cplOf(row);
            return {
                "Sl. No.": index + 1,
                "City Name": row.cityName || "",
                "Spend (₹)": toNumber(row.spend),
                Leads: toNumber(row.totalLeads),
                "CPL (₹)": cpl === null ? "-" : cpl.toFixed(2),
            };
        });
        if (grandTotalRow) {
            const cpl = cplOf(grandTotalRow);
            rows.push({
                "Sl. No.": "",
                "City Name": grandTotalRow.cityName,
                "Spend (₹)": toNumber(grandTotalRow.spend),
                Leads: toNumber(grandTotalRow.totalLeads),
                "CPL (₹)": cpl === null ? "-" : cpl.toFixed(2),
            });
        }
        return rows;
    }, [visibleRows, grandTotalRow]);

    // Colour a city's CPL against the overall average - purely relative, so a
    // city is only flagged when it is meaningfully off the blended rate.
    const cplTone = (cpl) => {
        if (cpl === null || !totals.cpl) return NEUTRAL;
        if (cpl <= totals.cpl * 0.9) return GOOD;
        if (cpl >= totals.cpl * 1.1) return BAD;
        return NEUTRAL;
    };

    const periodLabel = `${loadedPeriod.month} ${loadedPeriod.year}`;
    const hasRows = visibleRows.length > 0 || !!grandTotalRow;

    const renderSkeletonRows = () =>
        Array.from({ length: 6 }).map((_, i) => (
            <TableRow key={`sk-${i}`}>
                <TableCell className={classes.bodyCell} align="center">
                    <Skeleton variant="text" width={24} style={{ margin: "0 auto" }} />
                </TableCell>
                <TableCell className={classes.bodyCell}><Skeleton variant="text" width="55%" /></TableCell>
                <TableCell className={classes.bodyCell} align="right"><Skeleton variant="text" width="65%" style={{ marginLeft: "auto" }} /></TableCell>
                <TableCell className={classes.bodyCell} align="right"><Skeleton variant="text" width="40%" style={{ marginLeft: "auto" }} /></TableCell>
                <TableCell className={classes.bodyCell} align="right"><Skeleton variant="text" width="45%" style={{ marginLeft: "auto" }} /></TableCell>
            </TableRow>
        ));

    return (
        <div className={classes.pageRoot}>
            <div className={classes.banner}>
                <div className={classes.bannerIcon}>
                    <AccountBalanceWalletOutlinedIcon style={{ fontSize: 26 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={classes.bannerTitle}>Spend Report</div>
                    <div className={classes.bannerSubtitle}>
                        City-wise marketing spend, leads generated and cost per lead.
                    </div>
                </div>
                <div className={classes.bannerChip}>
                    <DateRangeOutlinedIcon style={{ fontSize: 16 }} />
                    {periodLabel}
                </div>
                <div className={classes.bannerRight}>
                    <MuiTooltip title="Refresh report">
                        <span>
                            <IconButton
                                size="small"
                                className={classes.refreshButton}
                                onClick={spendReport}
                                disabled={loading}
                            >
                                <RefreshIcon className={loading ? classes.spinning : undefined} style={{ fontSize: 20 }} />
                            </IconButton>
                        </span>
                    </MuiTooltip>
                    {lastUpdated && (
                        <div className={classes.lastUpdated}>Updated {moment(lastUpdated).format("hh:mm:ss A")}</div>
                    )}
                </div>
            </div>

            <Paper className={classes.filterCard}>
                <div className={classes.filterTitle}>
                    <TuneIcon fontSize="small" />
                    Filters
                </div>
                <div className={classes.filterRow}>
                    <FormControl variant="outlined" size="small" className={classes.field} style={{ minWidth: 180 }}>
                        <InputLabel>Month</InputLabel>
                        <Select label="Month" value={month} onChange={(e) => setMonth(e.target.value)} disabled={loading}>
                            {MONTHS.map((m) => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl variant="outlined" size="small" className={classes.field} style={{ minWidth: 150 }}>
                        <InputLabel>Year</InputLabel>
                        <Select label="Year" value={year} onChange={(e) => setYear(e.target.value)} disabled={loading}>
                            {yearList.map((y) => (
                                <MenuItem key={y} value={y}>{y}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        className={classes.primaryBtn}
                        onClick={spendReport}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    >
                        {loading ? "Loading…" : "Get Report"}
                    </Button>

                    <div style={{ marginLeft: "auto" }}>
                        {csvData.length && !loading ? (
                            <CSVLink
                                data={csvData}
                                filename={`Spend_Report_${loadedPeriod.month}_${loadedPeriod.year}_${moment().format("YYYY-MM-DD")}.csv`}
                                className={classes.csvBtn}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                    fontSize: 14,
                                    lineHeight: "24px",
                                }}
                            >
                                <GetAppIcon style={{ fontSize: 18 }} />
                                Download CSV
                            </CSVLink>
                        ) : (
                            <Button variant="contained" className={classes.csvBtn} disabled startIcon={<GetAppIcon />}>
                                Download CSV
                            </Button>
                        )}
                    </div>
                </div>
            </Paper>

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        classes={classes}
                        loading={loading}
                        label="Total Spend"
                        value={formatMoney(totals.spend)}
                        sub={periodLabel}
                        accent={ORANGE}
                        icon={<AccountBalanceWalletOutlinedIcon />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        classes={classes}
                        loading={loading}
                        label="Total Leads"
                        value={formatCount(totals.leads)}
                        sub={totals.leads ? "across all cities" : "no leads recorded"}
                        accent={BLUE}
                        icon={<PeopleAltOutlinedIcon />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        classes={classes}
                        loading={loading}
                        label="Average CPL"
                        value={totals.leads > 0 ? formatMoney(totals.cpl, 2) : "—"}
                        sub="blended cost per lead"
                        accent={TEAL}
                        icon={<LocalOfferOutlinedIcon />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        classes={classes}
                        loading={loading}
                        label="Cities"
                        value={formatCount(totals.cities)}
                        sub={search ? `${visibleRows.length} matching search` : "reporting spend"}
                        accent={PURPLE}
                        icon={<LocationCityOutlinedIcon />}
                    />
                </Grid>
            </Grid>

            <Card className={classes.tableCard}>
                {loading && <LinearProgress />}
                <div className={classes.tableToolbar}>
                    <div>
                        <div className={classes.tableTitle}>City-wise breakdown</div>
                        <div className={classes.tableSubtitle}>
                            {loading
                                ? "Fetching the latest numbers…"
                                : `${visibleRows.length} ${visibleRows.length === 1 ? "city" : "cities"} shown · sorted by ${
                                      COLUMNS.find((c) => c.id === sortBy)?.label ?? sortBy
                                  } (${sortDir === "asc" ? "ascending" : "descending"})`}
                        </div>
                    </div>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search city…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={classes.searchField}
                        style={{ minWidth: 240 }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ fontSize: 18, color: "#9aa4b4" }} />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch("")} style={{ padding: 4 }}>
                                        <ClearIcon style={{ fontSize: 15 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>

                <TableContainer className={classes.tableWrap}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" className={classes.headCell} style={{ width: 70 }}>
                                    Sl. No.
                                </TableCell>
                                {COLUMNS.map((col) => (
                                    <TableCell
                                        key={col.id}
                                        align={col.align}
                                        className={classes.headCell}
                                        sortDirection={sortBy === col.id ? sortDir : false}
                                    >
                                        <SortLabel
                                            active={sortBy === col.id}
                                            direction={sortBy === col.id ? sortDir : "asc"}
                                            onClick={() => handleSort(col.id)}
                                            disabled={loading}
                                        >
                                            {col.label}
                                        </SortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                renderSkeletonRows()
                            ) : !hasRows ? (
                                <TableRow>
                                    <TableCell colSpan={5} style={{ padding: 0, borderBottom: "none" }}>
                                        <div className={classes.emptyState}>
                                            <InboxOutlinedIcon className={classes.emptyIcon} />
                                            <div style={{ fontSize: 14, fontWeight: 600, color: "#6b7689" }}>
                                                {search ? "No cities match your search" : "No data available"}
                                            </div>
                                            <div style={{ fontSize: 12.5, marginTop: 4 }}>
                                                {search
                                                    ? "Try a different city name or clear the search."
                                                    : `Nothing was reported for ${periodLabel}. Try another month or year.`}
                                            </div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {visibleRows.map((row, index) => {
                                        const spend = toNumber(row.spend);
                                        const leads = toNumber(row.totalLeads);
                                        const cpl = cplOf(row);
                                        const share = totals.spend > 0 ? (spend / totals.spend) * 100 : 0;
                                        const tone = cplTone(cpl);

                                        return (
                                            <TableRow key={`${row.cityName}-${index}`} className={classes.bodyRow}>
                                                <TableCell className={classes.bodyCell} align="center" style={{ color: "#9aa4b4" }}>
                                                    {index + 1}
                                                </TableCell>
                                                <TableCell className={`${classes.bodyCell} ${classes.cityName}`}>
                                                    {row.cityName || "—"}
                                                </TableCell>
                                                <TableCell className={classes.bodyCell} align="right">
                                                    <div style={{ fontWeight: 600 }}>{formatMoney(spend)}</div>
                                                    <MuiTooltip title={`${share.toFixed(1)}% of total spend`}>
                                                        <div className={classes.shareBarTrack}>
                                                            <div
                                                                className={classes.shareBarFill}
                                                                style={{ width: `${Math.min(100, share)}%` }}
                                                            />
                                                        </div>
                                                    </MuiTooltip>
                                                </TableCell>
                                                <TableCell className={classes.bodyCell} align="right">
                                                    {formatCount(leads)}
                                                </TableCell>
                                                <TableCell className={classes.bodyCell} align="right">
                                                    {cpl === null ? (
                                                        <span style={{ color: "#b6bdc9" }}>—</span>
                                                    ) : (
                                                        <span
                                                            className={classes.cplChip}
                                                            style={{ backgroundColor: `${tone}16`, color: tone }}
                                                        >
                                                            {formatMoney(cpl, 2)}
                                                        </span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}

                                    {grandTotalRow && (
                                        <TableRow className={classes.totalRow}>
                                            <TableCell className={classes.bodyCell} align="center">—</TableCell>
                                            <TableCell className={classes.bodyCell}>{grandTotalRow.cityName}</TableCell>
                                            <TableCell className={classes.bodyCell} align="right">
                                                {formatMoney(grandTotalRow.spend)}
                                            </TableCell>
                                            <TableCell className={classes.bodyCell} align="right">
                                                {formatCount(grandTotalRow.totalLeads)}
                                            </TableCell>
                                            <TableCell className={classes.bodyCell} align="right">
                                                {cplOf(grandTotalRow) === null ? "—" : formatMoney(cplOf(grandTotalRow), 2)}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {!loading && hasRows && (
                    <div className={classes.legend}>
                        <span style={{ fontWeight: 600 }}>CPL vs overall average:</span>
                        <span className={classes.legendItem}>
                            <span className={classes.legendDot} style={{ backgroundColor: GOOD }} />
                            Better ({"≤"} 10% below)
                        </span>
                        <span className={classes.legendItem}>
                            <span className={classes.legendDot} style={{ backgroundColor: NEUTRAL }} />
                            In line
                        </span>
                        <span className={classes.legendItem}>
                            <span className={classes.legendDot} style={{ backgroundColor: BAD }} />
                            Costlier ({"≥"} 10% above)
                        </span>
                        <span style={{ marginLeft: "auto" }}>
                            Bar under each spend shows that city's share of total spend.
                        </span>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default connect(null, { setSnackInfo })(SpendReport);
